import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { minuteMs } from "verdi-common/timeConstants";
import { MasterIndex } from "verdi-web-client/Models";
import { IrrigationDeviceBase } from "verdi-web-client/Models/Devices/IrrigationDeviceBase";
import { useCreateZoneEvent } from "@/api/hooks/zone-events/use-create-zone-event";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useLocale } from "@/hooks/use-locale";
import { useSnackbar } from "@/hooks/use-snackbar/use-snackbar";
import { NAMESPACES } from "@/i18n/constants";
import type { DurationSource } from "@/services/analytics/events";
import { useOnDemandIrrigationEventTracker } from "@/services/analytics/irrigation/use-on-demand-irrigation-event-tracker";
import { OPERATING_MODE } from "@/services/device-attributes/attributes/operating-mode/definitions";
import { confirmModal } from "@/services/simple-modal";
import { formatTimeAsHoursMinutesAndSeconds } from "@/utils/time";
import { DurationPicker } from "./duration-picker";
import { FormContainer } from "./form-container";
import { FormValidationMessage } from "./form-validation-message";
import { useIrrigationPresets } from "./irrigation-presets";
import { ResponseTimeHint } from "./response-time-hint";
import { StartTimePicker } from "./start-time-picker";

const TIME_FORMAT_OPTIONS = {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
} as const;

const IMMEDIATE_START_BACKDATE_MS = minuteMs;

/**
 * Patch to correct for firmware issue that should be resolved ASAP. Do not keep this past July 1 2026.
 */
function resolveQuickAddStartTime(startTime: Date | null | undefined): Date {
    const now = new Date();
    if (startTime !== null && startTime !== undefined && startTime.valueOf() > now.valueOf() + minuteMs) {
        return startTime;
    }
    return new Date(now.valueOf() - IMMEDIATE_START_BACKDATE_MS);
}

interface EventInterval {
    startTime: Date;
    endTime: Date;
}

function checkOverlap(proposedStart: Date, proposedEnd: Date, events: EventInterval[]): boolean {
    return events.some((e) => {
        const start = e.startTime instanceof Date ? e.startTime : new Date(e.startTime);
        const end = e.endTime instanceof Date ? e.endTime : new Date(e.endTime);
        // Uses <, not <=, so an event starting exactly when another ends is safe.
        return proposedStart < end && start < proposedEnd;
    });
}

interface CreateEventFormProps {
    onClose: () => void;
    zoneId: string;
    manualDevices: IrrigationDeviceBase[];
    existingEvents?: EventInterval[];
    onPendingDurationChange?: (durationMs: number) => void;
    onPendingStartTimeChange?: (startTime: Date | null) => void;
    nowDate: Date;
}

export function CreateEventForm({
    onClose,
    zoneId,
    manualDevices,
    existingEvents = [],
    onPendingDurationChange,
    onPendingStartTimeChange,
    nowDate,
}: CreateEventFormProps) {
    const [durationMs, setDurationMs] = useState(0);
    const [durationError, setDurationError] = useState<string | null>(null);
    const [durationSource, setDurationSource] = useState<DurationSource>("preset");
    const [resolvedStartTime, setResolvedStartTime] = useState<Date | null>(null);
    const [startTimeError, setStartTimeError] = useState<string | null>(null);

    const { presets } = useIrrigationPresets(zoneId);
    const responseTimeMs = MasterIndex.zone.byID[zoneId]?.responseTimeStats?.theoreticalP95ActuationDelayMs;
    const { createZoneEvent, loading: createZoneEventLoading } = useCreateZoneEvent();
    const locale = useLocale();
    const { t } = useTranslation(NAMESPACES.FEATURES.CARDS, {
        keyPrefix: "zone.on-demand-irrigation.create-event-form",
    });
    const { t: tOnDemandValidationErrors } = useTranslation(NAMESPACES.FEATURES.CARDS, {
        keyPrefix: "zone.on-demand-irrigation.shared-validation-errors",
    });
    const { t: tManualModeWarning } = useTranslation(NAMESPACES.FEATURES.CARDS, {
        keyPrefix: "zone.on-demand-irrigation.manual-mode-warning",
    });
    const { t: tCommon } = useTranslation(NAMESPACES.COMMON);
    const { enqueueErrorSnackbar, enqueueSuccessSnackbar } = useSnackbar();
    const onDemandIrrigationEventTracker = useOnDemandIrrigationEventTracker(zoneId);

    const handleDurationChange = useCallback(
        (ms: number, error: string | null, source: DurationSource) => {
            setDurationMs(ms);
            setDurationError(error);
            setDurationSource(source);
            onPendingDurationChange?.(ms);
        },
        [onPendingDurationChange],
    );

    const handleStartTimeChange = useCallback(
        (startTime: Date | null, error: string | null) => {
            setStartTimeError(error);
            if (error !== null) return;
            setResolvedStartTime(startTime);
            onPendingStartTimeChange?.(startTime);
        },
        [onPendingStartTimeChange],
    );

    const displayedStartTime = resolvedStartTime ?? nowDate;
    const startTimeLabel = displayedStartTime.toLocaleTimeString(locale, TIME_FORMAT_OPTIONS);
    const endTimeLabel =
        durationMs > 0
            ? new Date(displayedStartTime.getTime() + durationMs).toLocaleTimeString(locale, TIME_FORMAT_OPTIONS)
            : null;

    const proposedEnd = durationMs > 0 ? new Date(displayedStartTime.getTime() + durationMs) : null;
    const overlapError =
        proposedEnd !== null && checkOverlap(displayedStartTime, proposedEnd, existingEvents)
            ? tOnDemandValidationErrors("event-overlap")
            : null;

    const executeCreateEvent = useCallback(async () => {
        const currentDate = new Date();
        const baseStartTime = resolvedStartTime ?? currentDate;
        const endTimeValue = baseStartTime.valueOf() + durationMs;
        const correctedStartTime = resolveQuickAddStartTime(resolvedStartTime);
        // For "start now", resolveQuickAddStartTime backdates the start — track the duration
        // actually sent to the API, not the form's nominal durationMs.
        const createdDurationMs = endTimeValue - correctedStartTime.valueOf();
        const event = await createZoneEvent({
            zoneWateringPercentages: { [zoneId]: 1 },
            defaultDuration: createdDurationMs,
            rule: undefined,
            zoneIrrigationOffsets: undefined,
            options: undefined,
            startTime: correctedStartTime,
        });
        if (event) {
            onDemandIrrigationEventTracker.trackCreate({
                eventId: event.id,
                durationMs: createdDurationMs,
                isScheduledStart: resolvedStartTime !== null,
                durationSource,
            });
            enqueueSuccessSnackbar(
                t("snackbar.success", {
                    timeLabelWithUnit: formatTimeAsHoursMinutesAndSeconds(durationMs, {
                        includeSecondsWithMinutes: false,
                    }),
                }),
            );
            onClose();
        } else {
            onDemandIrrigationEventTracker.trackCreateFail();
            enqueueErrorSnackbar(t("snackbar.error"));
        }
    }, [
        createZoneEvent,
        zoneId,
        durationMs,
        durationSource,
        resolvedStartTime,
        enqueueSuccessSnackbar,
        enqueueErrorSnackbar,
        onDemandIrrigationEventTracker,
        t,
        onClose,
    ]);

    const onClickCreateEvent = useCallback(async () => {
        if (createZoneEventLoading) return;

        if (manualDevices.length === 0) {
            await executeCreateEvent();
            return;
        }

        const startTiming = resolvedStartTime
            ? formatTimeAsHoursMinutesAndSeconds(resolvedStartTime.getTime() - nowDate.getTime(), {
                  includeSecondsWithMinutes: false,
              })
            : undefined;

        confirmModal({
            title: tManualModeWarning("title"),
            body: (
                <div className={"text-sm gap-2 flex flex-col"}>
                    <p>{tManualModeWarning("confirm-description")}</p>
                    <ul className={"list-disc list-inside font-medium"}>
                        {manualDevices.map((device) => (
                            <li key={device.id}>{device.name}</li>
                        ))}
                    </ul>
                </div>
            ),
            confirmLabel: startTiming
                ? tManualModeWarning("confirm-label-delayed", { startTiming })
                : tManualModeWarning("confirm-label-now"),
            cancelLabel: tCommon("actions.cancel"),
            onConfirm: async () => {
                const nonSchedulableDevices = manualDevices.filter((device) => !device.supportsSettingManualMode);
                if (nonSchedulableDevices.length > 0) {
                    enqueueErrorSnackbar(
                        tManualModeWarning("confirm-error-not-schedulable", {
                            deviceNames: nonSchedulableDevices.map((device) => device.name).join(", "),
                        }),
                    );
                    return;
                }

                try {
                    await Promise.all(manualDevices.map((device) => device.setManualMode(OPERATING_MODE.SCHEDULE)));
                } catch {
                    enqueueErrorSnackbar(tManualModeWarning("confirm-error"));
                    return;
                }

                // executeCreateEvent handles its own errors
                await executeCreateEvent();
            },
        });
    }, [
        createZoneEventLoading,
        manualDevices,
        executeCreateEvent,
        enqueueErrorSnackbar,
        tManualModeWarning,
        tCommon,
        resolvedStartTime,
        nowDate,
    ]);

    const createEventButtonLabel = resolvedStartTime
        ? t("buttons.create-delayed", {
              delayLabelWithUnit: formatTimeAsHoursMinutesAndSeconds(resolvedStartTime.getTime() - nowDate.getTime(), {
                  includeSecondsWithMinutes: false,
              }),
          })
        : t("buttons.create-now", {
              nowLabel: t("start-time.earliest-start-button"),
          });

    return (
        <FormContainer title={t("title")} onClose={onClose} variant={"create"}>
            <div className={"flex flex-col gap-4"}>
                <DurationPicker onDurationChange={handleDurationChange} presets={presets.duration} />
                <StartTimePicker onStartTimeChange={handleStartTimeChange} presets={presets.startTime} />
            </div>
            {overlapError && <FormValidationMessage errorMessage={overlapError} />}
            {endTimeLabel && (
                <div className={"text-muted-foreground flex flex-row items-center justify-between w-full gap-2"}>
                    <span>{t("event-time")}:</span>
                    <p className={"tabular-nums"}>
                        {startTimeLabel} – {endTimeLabel} (
                        {formatTimeAsHoursMinutesAndSeconds(durationMs, { includeSecondsWithMinutes: false })})
                    </p>
                </div>
            )}
            <div className={"flex flex-col items-center jusity-center gap-1"}>
                <Button
                    className={"w-full p-3"}
                    disabled={
                        durationMs <= 0 ||
                        durationError !== null ||
                        startTimeError !== null ||
                        overlapError !== null ||
                        createZoneEventLoading
                    }
                    onClick={onClickCreateEvent}
                >
                    {createZoneEventLoading ? <Spinner className={"size-4"} /> : createEventButtonLabel}
                </Button>
                <ResponseTimeHint responseTimeMs={responseTimeMs} className={"mt-0"} />
            </div>
        </FormContainer>
    );
}
